import { useCreateShipmentFormContext } from "inbounds/createShipment/useCreateShipmentFormContext";
import { CreateShipmentInputName } from "inbounds/createShipment/useCreateShipmentForm";
import { batch, useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { getSelectedProducts } from "../../../../store/selector/getSelectedProducts";
import { removeStorageProduct, addBulkUploadProduct } from "../../../SelectStorageProductsActions";
import { getStorageItemDetails } from "../../../getStorageItemDetails";
import { ProductWithQuantity } from "../types";
import { log } from "@deliverr/commons-logs";
import { BATCH_PROCESSING, chunkArray } from "common/constants/processing.const";

export const useProductOperations = () => {
  const dispatch = useDispatch();
  const selectedProducts = useSelector(getSelectedProducts);
  const { setValue } = useCreateShipmentFormContext();

  const removeExistingProducts = useCallback(() => {
    if (!selectedProducts?.length) {
      return;
    }

    try {
      // Filter valid products once
      const validSelectedProducts = selectedProducts.filter((product) => product?.dsku);
      if (!validSelectedProducts.length) {
        return;
      }

      // Process removals in chunks for better performance with large datasets
      const productChunks = chunkArray(validSelectedProducts, BATCH_PROCESSING.REDUX_ACTION_BATCH_SIZE);

      productChunks.forEach((chunk, chunkIndex) => {
        // Process each chunk in a batch
        batch(() => {
          chunk.forEach((product) => {
            try {
              dispatch(removeStorageProduct(product.dsku));
              setValue(`${CreateShipmentInputName.SINGLE_SKU_PACKAGES}.${product.dsku}`, []);
            } catch (error) {
              log.error(`Failed to remove product ${product.dsku}:`, error);
              throw error;
            }
          });
        });
      });
    } catch (error) {
      log.error("Failed to remove existing products:", error);
      throw error;
    }
  }, [dispatch, selectedProducts, setValue]);

  const addNewProducts = useCallback(
    (products: ProductWithQuantity[]) => {
      if (!products?.length) {
        log.warn("No products provided to add");
        return;
      }

      try {
        // Products are already filtered in the caller
        const validProducts = products.filter((product) => product?.dsku);
        if (!validProducts.length) {
          log.warn("No valid products to add");
          return;
        }

        // Process additions in optimized chunks
        const productChunks = chunkArray(validProducts, BATCH_PROCESSING.REDUX_ACTION_BATCH_SIZE);

        productChunks.forEach((chunk) => {
          batch(() => {
            chunk.forEach((product) => {
              try {
                dispatch(addBulkUploadProduct(product, undefined));
              } catch (error) {
                log.error(`Failed to add product ${product.dsku}:`, error);
                throw error;
              }
            });
          });
        });

        // Single dispatch for storage item details for all products (already optimized)
        try {
          dispatch(getStorageItemDetails(validProducts));
        } catch (error) {
          log.error("Failed to fetch storage item details:", error);
        }
      } catch (error) {
        log.error("Failed to add new products:", error);
        throw error;
      }
    },
    [dispatch]
  );

  return {
    removeExistingProducts,
    addNewProducts,
  };
};
